// src/pages/Admin/area-notifications/ComposeForm.tsx
//
// Compose form for an area-broadcast push notification:
//   • Title / Body / Deep-link URL fields with character validation.
//   • Market preset dropdown (snaps the pin/radius to the selected market).
//   • Custom-area toggle that hands authority to the radius slider + draggable
//     pin on the embedded AreaMap.
//
// Styling mirrors the rest of the admin site (CreateBusinessModal et al.):
// dark gray inputs with a violet focus ring, uppercase gray-400 labels, and
// the violet→pink gradient primary button used everywhere as the page CTA.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AreaMap } from './AreaMap';

// ─── Public types ─────────────────────────────────────────────────────────────

export interface MarketOption {
  id: string;
  name: string;
  center_lat: number;
  center_lng: number;
  radius_mi: number;
}

export interface DraftBroadcast {
  title: string;
  body: string;
  deepLinkUrl: string;
  marketId: string | null;
  customArea: boolean;
  centerLat: number;
  centerLng: number;
  radiusMi: number;
}

interface ComposeFormProps {
  markets: MarketOption[];
  initial?: Partial<DraftBroadcast>;
  onPreview: (draft: DraftBroadcast) => void;
}

// ─── Constants & shared classes ──────────────────────────────────────────────

const TITLE_MAX = 80;
const BODY_MAX = 500;
const DEEP_LINK_MAX = 2048;

const inputClass =
  'w-full px-3.5 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all';
const selectClass = `${inputClass} appearance-none`;

function Label({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
      {children}
      {required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildInitialDraft(
  markets: MarketOption[],
  initial: Partial<DraftBroadcast> | undefined,
): DraftBroadcast {
  const initialMarketId = initial?.marketId ?? null;
  const fallbackMarket =
    (initialMarketId && markets.find(m => m.id === initialMarketId)) ||
    markets[0] ||
    null;

  // Center/radius defaults: prefer explicit `initial`, then the resolved market,
  // then a safe US-centered placeholder if no markets were provided.
  const centerLat =
    initial?.centerLat ?? fallbackMarket?.center_lat ?? 39.8283;
  const centerLng =
    initial?.centerLng ?? fallbackMarket?.center_lng ?? -98.5795;
  const radiusMi = initial?.radiusMi ?? fallbackMarket?.radius_mi ?? 5;

  return {
    title: initial?.title ?? '',
    body: initial?.body ?? '',
    deepLinkUrl: initial?.deepLinkUrl ?? '',
    marketId: fallbackMarket ? fallbackMarket.id : null,
    customArea: initial?.customArea ?? false,
    centerLat,
    centerLng,
    radiusMi,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ComposeForm(props: ComposeFormProps): React.ReactElement {
  const { markets, initial, onPreview } = props;

  const [draft, setDraft] = useState<DraftBroadcast>(() =>
    buildInitialDraft(markets, initial),
  );

  // When the selected market changes (and we're not in custom-area mode), snap
  // center/radius to that market. Also handles the custom→preset toggle: when
  // customArea flips off, re-snap to the currently selected market.
  useEffect(() => {
    if (draft.customArea) return;
    if (!draft.marketId) return;
    const market = markets.find(m => m.id === draft.marketId);
    if (!market) return;
    if (
      draft.centerLat === market.center_lat &&
      draft.centerLng === market.center_lng &&
      draft.radiusMi === market.radius_mi
    ) {
      return;
    }
    setDraft(d => ({
      ...d,
      centerLat: market.center_lat,
      centerLng: market.center_lng,
      radiusMi: market.radius_mi,
    }));
  }, [
    draft.marketId,
    draft.customArea,
    draft.centerLat,
    draft.centerLng,
    draft.radiusMi,
    markets,
  ]);

  // ─── Field handlers ────────────────────────────────────────────────────────

  const setField = useCallback(
    <K extends keyof DraftBroadcast>(key: K, value: DraftBroadcast[K]) => {
      setDraft(d => ({ ...d, [key]: value }));
    },
    [],
  );

  const handleCenterChange = useCallback(
    (lat: number, lng: number) => {
      // The map only fires drag events when draggable={customArea}. Guard anyway
      // so the toggle stays the authority on whether dragging mutates state.
      if (!draft.customArea) return;
      setDraft(d => ({ ...d, centerLat: lat, centerLng: lng }));
    },
    [draft.customArea],
  );

  // ─── Address geocoding (custom area) ───────────────────────────────────────

  const [addressInput, setAddressInput] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);

  const geocodeAddress = useCallback(async () => {
    const q = addressInput.trim();
    if (!q) return;
    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token) {
      setGeocodeError('Mapbox token not configured');
      return;
    }
    setGeocoding(true);
    setGeocodeError(null);
    try {
      const url =
        `https://api.mapbox.com/geocoding/v5/mapbox.places/` +
        `${encodeURIComponent(q)}.json?limit=1&access_token=${encodeURIComponent(token)}`;
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`Mapbox HTTP ${resp.status}`);
      const data = await resp.json();
      const feature = data?.features?.[0];
      if (!feature?.center || feature.center.length !== 2) {
        setGeocodeError('No match found for that address');
        return;
      }
      const [lng, lat] = feature.center as [number, number];
      setDraft(d => ({ ...d, centerLat: lat, centerLng: lng }));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Geocoding failed';
      setGeocodeError(msg);
    } finally {
      setGeocoding(false);
    }
  }, [addressInput]);

  // ─── Validation ────────────────────────────────────────────────────────────

  const titleInvalid = draft.title.length < 1 || draft.title.length > TITLE_MAX;
  const bodyInvalid = draft.body.length < 1 || draft.body.length > BODY_MAX;
  const deepLinkInvalid = draft.deepLinkUrl.length > DEEP_LINK_MAX;
  const previewDisabled = titleInvalid || bodyInvalid || deepLinkInvalid;

  const selectedMarket = useMemo(
    () => markets.find(m => m.id === draft.marketId) ?? null,
    [markets, draft.marketId],
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      {/* Title */}
      <div>
        <Label required>Title</Label>
        <input
          type="text"
          value={draft.title}
          onChange={e => setField('title', e.target.value.slice(0, TITLE_MAX))}
          placeholder="e.g. Tonight only — half-off appetizers"
          maxLength={TITLE_MAX}
          className={inputClass}
        />
        <p className="text-xs text-gray-500 mt-1">
          {draft.title.length}/{TITLE_MAX}
        </p>
      </div>

      {/* Body */}
      <div>
        <Label required>Body</Label>
        <textarea
          value={draft.body}
          onChange={e => setField('body', e.target.value.slice(0, BODY_MAX))}
          placeholder="Notification message body"
          rows={3}
          maxLength={BODY_MAX}
          className={`${inputClass} resize-y`}
        />
        <p className="text-xs text-gray-500 mt-1">
          {draft.body.length}/{BODY_MAX}
        </p>
      </div>

      {/* Deep link */}
      <div>
        <Label>Deep link URL</Label>
        <input
          type="text"
          value={draft.deepLinkUrl}
          onChange={e =>
            setField('deepLinkUrl', e.target.value.slice(0, DEEP_LINK_MAX))
          }
          placeholder="socialspark://… or https://…"
          maxLength={DEEP_LINK_MAX}
          className={inputClass}
        />
        <p className="text-xs text-gray-500 mt-1">
          Optional. {draft.deepLinkUrl.length}/{DEEP_LINK_MAX}
        </p>
      </div>

      {/* Area section */}
      <div className="space-y-4 p-4 bg-gray-800/40 border border-gray-700/60 rounded-xl">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-white">Area</h3>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={draft.customArea}
              onChange={e => setField('customArea', e.target.checked)}
              className="sr-only"
            />
            <div
              className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                draft.customArea
                  ? 'bg-violet-600 border-violet-600'
                  : 'border-gray-600 bg-gray-800'
              }`}
            >
              {draft.customArea && (
                <svg
                  className="w-2.5 h-2.5 text-white"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M2 6l3 3 5-5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <span className="text-xs text-gray-300">Custom area</span>
          </label>
        </div>

        {/* Market preset */}
        <div>
          <Label>Market preset</Label>
          <select
            value={draft.marketId ?? ''}
            onChange={e => setField('marketId', e.target.value || null)}
            disabled={draft.customArea}
            className={`${selectClass} ${
              draft.customArea ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          >
            {markets.length === 0 && <option value="">No markets</option>}
            {markets.map(m => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          {draft.customArea && (
            <p className="text-xs text-gray-500 mt-1">
              Custom area is on — pin and radius below are authoritative.
            </p>
          )}
        </div>

        {/* Address search (custom area only) */}
        {draft.customArea && (
          <div>
            <Label>Address</Label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Type an address or place name…"
                value={addressInput}
                onChange={e => {
                  setAddressInput(e.target.value);
                  if (geocodeError) setGeocodeError(null);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void geocodeAddress();
                  }
                }}
                className={inputClass}
              />
              <button
                type="button"
                onClick={() => void geocodeAddress()}
                disabled={!addressInput.trim() || geocoding}
                className="px-4 py-2.5 text-sm font-medium text-gray-200 bg-gray-800 border border-gray-700 rounded-xl hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all whitespace-nowrap"
              >
                {geocoding ? 'Searching…' : 'Find'}
              </button>
            </div>
            {geocodeError ? (
              <p className="text-xs text-red-400 mt-1">{geocodeError}</p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                Center at: ({draft.centerLat.toFixed(4)}, {draft.centerLng.toFixed(4)})
              </p>
            )}
          </div>
        )}

        {/* Radius slider */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label>Radius</Label>
            <span className="text-xs text-gray-300 tabular-nums">
              {draft.radiusMi.toFixed(1)} mi
            </span>
          </div>
          <input
            type="range"
            min={0.5}
            max={25}
            step={0.1}
            value={draft.radiusMi}
            onChange={e => setField('radiusMi', parseFloat(e.target.value))}
            disabled={!draft.customArea}
            className={`w-full accent-violet-500 ${
              draft.customArea ? '' : 'opacity-40 cursor-not-allowed'
            }`}
          />
          <div className="flex justify-between text-[10px] text-gray-500 mt-0.5">
            <span>0.5 mi</span>
            <span>25 mi</span>
          </div>
        </div>

        {/* Map */}
        <AreaMap
          centerLat={draft.centerLat}
          centerLng={draft.centerLng}
          radiusMi={draft.radiusMi}
          draggable={draft.customArea}
          onCenterChange={handleCenterChange}
        />

        {selectedMarket && !draft.customArea && (
          <p className="text-xs text-gray-500">
            Snapped to <span className="text-gray-300">{selectedMarket.name}</span>{' '}
            ({selectedMarket.center_lat.toFixed(4)},{' '}
            {selectedMarket.center_lng.toFixed(4)} · {selectedMarket.radius_mi}
            {' mi'}).
          </p>
        )}
        {draft.customArea && (
          <p className="text-xs text-gray-500">
            Drag the pin to move the center. Center:{' '}
            <span className="text-gray-300 tabular-nums">
              {draft.centerLat.toFixed(4)}, {draft.centerLng.toFixed(4)}
            </span>
          </p>
        )}
      </div>

      {/* Preview button */}
      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={() => onPreview(draft)}
          disabled={previewDisabled}
          className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-pink-600 rounded-xl hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
        >
          Preview audience
        </button>
      </div>
    </div>
  );
}

export default ComposeForm;
